import { useEffect, useRef, useState } from "react";
import { useAsyncDebounce } from "react-table";
/** Use `useIsResizing` to check, if resizing is in progress */
export const useIsResizing = (size, options = {}) => {
    const { onStart = () => { }, onResize = () => { }, onEnd = () => { }, debounceEndTime = 100, } = options;
    const [isResizing, setIsResizing] = useState(false);
    const initialSize = useRef(true);
    const debouncedEnd = useAsyncDebounce(() => {
        setIsResizing(false);
        onEnd();
    }, debounceEndTime);
    /**
     * Sizes usually have the following value states:
     *
     * 1. Undefined : HTML element is not detected yet -> no resize
     * 2. 123px : initial detected size -> no actual resize
     * 3. 124px : size changed -> resize!
     */
    useEffect(() => {
        // 1)
        if (!size) {
            return;
        }
        // 2)
        if (initialSize.current) {
            initialSize.current = false;
            return;
        }
        // 3)
        if (!isResizing) {
            onStart();
            setIsResizing(true);
        }
        // this function call will be interrupted by immediately subsequent calls
        debouncedEnd();
    }, [size, setIsResizing, debouncedEnd]);
    /** Call `onResize` depending on state */
    useEffect(() => {
        if (isResizing) {
            onResize();
        }
    }, [size, isResizing]);
    return isResizing;
};
export default useIsResizing;
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoidXNlSXNSZXNpemluZy5qcyIsInNvdXJjZVJvb3QiOiIiLCJzb3VyY2VzIjpbIi4uLy4uL3NyYy9ob29rcy91c2VJc1Jlc2l6aW5nLnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUFBLE9BQU8sRUFBRSxTQUFTLEVBQUUsTUFBTSxFQUFFLFFBQVEsRUFBRSxNQUFNLE9BQU8sQ0FBQztBQUNwRCxPQUFPLEVBQUUsZ0JBQWdCLEVBQUUsTUFBTSxhQUFhLENBQUM7QUFnQi9DLCtEQUErRDtBQUMvRCxNQUFNLENBQUMsTUFBTSxhQUFhLEdBQUcsQ0FDM0IsSUFBc0IsRUFDdEIsVUFBZ0MsRUFBRSxFQUN6QixFQUFFO0lBQ1gsTUFBTSxFQUNKLE9BQU8sR0FBRyxHQUFHLEVBQUUsR0FBRSxDQUFDLEVBQ2xCLFFBQVEsR0FBRyxHQUFHLEVBQUUsR0FBRSxDQUFDLEVBQ25CLEtBQUssR0FBRyxHQUFHLEVBQUUsR0FBRSxDQUFDLEVBQ2hCLGVBQWUsR0FBRyxHQUFHLEdBQ3RCLEdBQUcsT0FBTyxDQUFDO0lBRVosTUFBTSxDQUFDLFVBQVUsRUFBRSxhQUFhLENBQUMsR0FBRyxRQUFRLENBQUMsS0FBSyxDQUFDLENBQUM7SUFDcEQsTUFBTSxXQUFXLEdBQUcsTUFBTSxDQUFDLElBQUksQ0FBQyxDQUFDO0lBRWpDLE1BQU0sWUFBWSxHQUFHLGdCQUFnQixDQUFDLEdBQUcsRUFBRTtRQUN6QyxhQUFhLENBQUMsS0FBSyxDQUFDLENBQUM7UUFDckIsS0FBSyxFQUFFLENBQUM7SUFDVixDQUFDLEVBQUUsZUFBZSxDQUFDLENBQUM7SUFFcEI7Ozs7OztPQU1HO0lBQ0gsU0FBUyxDQUFDLEdBQUcsRUFBRTtRQUNiLEtBQUs7UUFDTCxJQUFJLENBQUMsSUFBSSxFQUFFLENBQUM7WUFDVixPQUFPO1FBQ1QsQ0FBQztRQUVELEtBQUs7UUFDTCxJQUFJLFdBQVcsQ0FBQyxPQUFPLEVBQUUsQ0FBQztZQUN4QixXQUFXLENBQUMsT0FBTyxHQUFHLEtBQUssQ0FBQztZQUM1QixPQUFPO1FBQ1QsQ0FBQztRQUVELEtBQUs7UUFDTCxJQUFJLENBQUMsVUFBVSxFQUFFLENBQUM7WUFDaEIsT0FBTyxFQUFFLENBQUM7WUFDVixhQUFhLENBQUMsSUFBSSxDQUFDLENBQUM7UUFDdEIsQ0FBQztRQUVELHlFQUF5RTtRQUN6RSxZQUFZLEVBQUUsQ0FBQztJQUNqQixDQUFDLEVBQUUsQ0FBQyxJQUFJLEVBQUUsYUFBYSxFQUFFLFlBQVksQ0FBQyxDQUFDLENBQUM7SUFFeEMseUNBQXlDO0lBQ3pDLFNBQVMsQ0FBQyxHQUFHLEVBQUU7UUFDYixJQUFJLFVBQVUsRUFBRSxDQUFDO1lBQ2YsUUFBUSxFQUFFLENBQUM7UUFDYixDQUFDO0lBQ0gsQ0FBQyxFQUFFLENBQUMsSUFBSSxFQUFFLFVBQVUsQ0FBQyxDQUFDLENBQUM7SUFFdkIsT0FBTyxVQUFVLENBQUM7QUFDcEIsQ0FBQyxDQUFDO0FBRUYsZUFBZSxhQUFhLENBQUMifQ==